import { useEffect, useRef, useState } from "react";

/**
 * Looping background video for the marketing site.
 *
 * Reel composition — every clip is a baseball player making a throw
 * (verified mixkit baseball stock):
 *   - 856  · pitcher delivery from the mound
 *   - 881  · pitcher mound delivery + finish
 *   - 858  · position-player (infielder/outfielder) catches & throws
 *
 * No batting, sliding, or generic stock — every reel reinforces the
 * "Every Throw. Every Player." positioning.
 *
 * Two render modes:
 *   - `fixed=false` (default) → absolute inset-0 inside whatever section
 *     wraps it. Used for hero / per-section embeds.
 *   - `fixed=true` → position:fixed full-viewport, lives at the page root
 *     and stays anchored as the visitor scrolls. Sections above sit on top
 *     of it via z-index layering.
 */
const THROW_VIDEOS = [
  "https://assets.mixkit.co/videos/856/856-720.mp4",  // Pitcher · mound delivery
  "https://assets.mixkit.co/videos/881/881-720.mp4",  // Pitcher · full delivery + finish
  "https://assets.mixkit.co/videos/858/858-720.mp4",  // Position player · catch + throw
];

const POSTERS = [
  "https://assets.mixkit.co/videos/856/856-thumb-720-0.jpg",
  "https://assets.mixkit.co/videos/881/881-thumb-720-0.jpg",
  "https://assets.mixkit.co/videos/858/858-thumb-720-0.jpg",
];

export default function BackgroundVideo({ tintClass = "video-tint", className = "", fixed = false }) {
  const [idx, setIdx] = useState(0);
  const [failedIndexes, setFailedIndexes] = useState(() => new Set());
  const ref = useRef(null);

  const allFailed = failedIndexes.size >= THROW_VIDEOS.length;

  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    const onEnded = () => advance();
    v.addEventListener("ended", onEnded);
    return () => v.removeEventListener("ended", onEnded);
  }, [idx]);

  const advance = () => {
    setIdx((i) => {
      let next = (i + 1) % THROW_VIDEOS.length;
      let safety = 0;
      while (failedIndexes.has(next) && safety < THROW_VIDEOS.length) {
        next = (next + 1) % THROW_VIDEOS.length;
        safety++;
      }
      return next;
    });
  };

  const handleError = () => {
    setFailedIndexes((prev) => {
      const ns = new Set(prev);
      ns.add(idx);
      return ns;
    });
    setTimeout(advance, 60);
  };

  const positionClass = fixed
    ? "fixed inset-0 overflow-hidden pointer-events-none"
    : "absolute inset-0 overflow-hidden pointer-events-none";
  const fixedStyle = fixed ? { zIndex: 0 } : undefined;

  return (
    <div
      className={`${positionClass} ${className}`}
      style={fixedStyle}
      aria-hidden="true"
      data-testid={fixed ? "site-background-video" : undefined}
    >
      {!allFailed && (
        <video
          ref={ref}
          key={`${idx}-${THROW_VIDEOS[idx]}`}
          poster={POSTERS[idx]}
          autoPlay
          muted
          playsInline
          preload="auto"
          crossOrigin="anonymous"
          onError={handleError}
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="background-video"
        >
          <source src={THROW_VIDEOS[idx]} type="video/mp4" />
        </video>
      )}
      {allFailed && (
        <img src={POSTERS[0]} alt="" className="absolute inset-0 w-full h-full object-cover" />
      )}
      <div className={`absolute inset-0 ${tintClass}`} />
    </div>
  );
}
